import { useAppConstantsContext, AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  useFamilyTreeReports,
  AddToFamilyTreeButton,
  formatFamilyTreeDataFromObituaryReport,
  useFocusedFamilyTreeMember,
} from '@ltvco/refresh-lib/fmt';
import { useLLMObituaryParser, useReport } from '@ltvco/refresh-lib/hooks';
import { Card, Grid, Box, styled } from '@ltvco/refresh-lib/theme';
import {
  isZeroed,
  useScrollToSectionOnNavigate,
} from '@ltvco/refresh-lib/utils';
import {
  DebugMenu,
  FullObituary,
  ObituaryFuneralDetails,
  ObituaryGeneralInformation,
  ObituaryHeader,
  ObituaryLifeEvents,
  ObituaryPersonalInterest,
  ObituaryRelatives,
  ObituaryReport,
  ObituaryReportLoading,
  ObituarySmartInsight,
  PersonSearchCta,
  ReportLoading,
} from '@ltvco/refresh-lib/v1';
import { ReportNullState } from '@ltvco/refresh-lib/v2';
import { useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { constants } from 'appConstants';

import type { ReportRouteProps } from 'components/reports/types';

interface ObituaryReportProps extends ReportRouteProps {}

function parseDate(date: string) {
  if (!date) {
    return false;
  }
  const [month, day, year] = date.split('/').map(Number);
  return {
    full: date,
    parsed: {
      year,
      month,
      day,
    },
  };
}

const StyledContainer = styled(Box)(({ theme }) => ({
  margin: 'auto',
  padding: theme.spacing(2),
  borderRadius: '20px',
  backgroundColor: theme.palette.background.paper,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    padding: '20px',
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: 'none',
  backgroundRepeat: 'repeat',
  backgroundSize: 'contain',
  backgroundPosition: 'center',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: '0',
  [theme.breakpoints.up('sm')]: {
    padding: '80px 40px 20px 40px',
  },
}));

export function ObituaryChatBvReport({ permalink }: ObituaryReportProps) {
  const {
    data: { brandName },
  } = useAppConstantsContext();
  const queryResult = useReport(permalink);
  const zeroed = isZeroed(queryResult);
  const { hash } = useLocation();
  const { trackEvent } = useContext(AppConfig);
  const report = new ObituaryReport(queryResult?.data as any);
  const obituary = report.data.obituaries[0];
  const { navigateToFocusedMember } = useFocusedFamilyTreeMember();
  const { data: reportsData } = useFamilyTreeReports({
    bodyOptions: {
      report_type: ['ObituaryReport'],
      placeholders_excluded: true,
    },
    logNotFoundErrorEnabled: false,
  });

  const navigate = useNavigate();

  const { data: llmResponse, isLoading } = useLLMObituaryParser({
    enabled: Boolean(obituary),
    obituary,
    customSufixStorageKey: 'obituaryParser',
    shouldSuspendExecution:
      !constants.featureFlags.obituaryParserEnabled || Boolean(!obituary),
  });

  useEffect(() => {
    trackEvent('view full report', 'view', 'obituary search');
  }, [trackEvent, permalink]);

  const llmAccomplishments = llmResponse?.data?.accomplishments || [];
  const llmEducation = llmResponse?.data?.educationLevel || [];
  const llmCareer = llmResponse?.data?.jobs || [];
  const llmAge = llmResponse?.data?.age || '';
  const llmParsedNicknames = llmResponse?.data?.nicknames?.join('') || '';
  const llmRelatives = llmResponse?.data?.relatives || [];
  const llmService = llmResponse?.data?.service || null;
  const llmVisitation = llmResponse?.data?.visitation || null;
  const llmDonations = llmResponse?.data?.donations || [];
  const activities = llmResponse?.data?.activities || [];
  const hobbies = llmResponse?.data?.hobbies || [];
  const miscellaneous = llmResponse?.data?.miscellaneous || [];
  const llmParsedDOB = parseDate(llmResponse?.data?.dateOfBirth) || null;
  const llmParsedDOD = parseDate(llmResponse?.data?.dateOfDeath) || null;
  const llmLocation = llmResponse?.data?.location || null;
  const llmPlaceOfBirth = llmResponse?.data?.placeOfBirth || null;

  useScrollToSectionOnNavigate(hash, queryResult);

  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={2} />;
  }
  if (zeroed) {
    return <ReportNullState />;
  }

  if (!report) {
    return <ReportLoading menuItems={2} />;
  }

  if (!report?.data?.obituaries && !report?.data?.obituaries?.length) {
    return <ReportNullState />;
  }

  const {
    id,
    name,
    dob,
    dod,
    funeralHome,
    funeralHomeAddress,
    funeralHomeUrl,
    message,
    photoUrl,
    nickname,
    personUrl,
    recentCity,
    recentRegion,
  } = obituary;

  document.title = `${name} - ${brandName}`;

  const reportInFamilyTree: boolean | undefined = reportsData?.reports
    ? !!reportsData?.reports?.some((report) => {
        return 'obituary_id' in report && report.obituary_id === id;
      })
    : undefined;

  const locationValue =
    recentCity && recentRegion
      ? `${recentCity}, ${recentRegion}`
      : llmLocation || '';

  const handleAddingRelativeToFamilyTree = async () => {
    if (!obituary) {
      return null;
    }

    const reportIndi = formatFamilyTreeDataFromObituaryReport(
      id,
      name,
      dob || llmParsedDOB,
      dod || llmParsedDOD,
      recentCity,
      recentRegion,
      permalink,
      photoUrl
    );

    if (reportInFamilyTree) {
      navigateToFocusedMember(memberIdInFamilyTree as number);
      return;
    }

    localStorage.setItem('familyTreeRelativeToAdd', JSON.stringify(reportIndi));

    // Obituary report is not posting to ancestry tree
    // Instead redirect to the family tree page
    // so the user can search for a person report
    // and create a family tree for the person
    // This is the only report that is not posting to ancestry tree
    navigate('/family-tree');
  };

  const memberIdInFamilyTree = reportInFamilyTree
    ? reportsData?.reports?.find((report) => {
        return 'obituary_id' in report && report.obituary_id === id;
      })?.member_id
    : undefined;

  return (
    <>
      <Grid size={{ sm: 12, md: 8, lg: 8 }} marginTop={3}>
        <StyledCard
          id="obituary-overview"
          style={{
            backgroundImage: `url(${constants.images.imgTreeOfLife})`,
          }}
        >
          <StyledContainer>
            <ObituaryHeader
              personName={name}
              nickname={nickname || llmParsedNicknames}
              personImage={photoUrl}
              age={llmAge}
              dob={dob || llmParsedDOB}
              dod={dod || llmParsedDOD}
              funeralHome={funeralHome}
              funeralHomeAddress={funeralHomeAddress}
              funeralHomeUrl={funeralHomeUrl}
              photoUrl={photoUrl}
              personUrl={personUrl}
            />
            <AddToFamilyTreeButton
              reportInFamilyTree={reportInFamilyTree}
              addRelativeToFamilyTree={handleAddingRelativeToFamilyTree}
            />
            <ObituaryGeneralInformation
              dob={dob || llmParsedDOB}
              dod={dod || llmParsedDOD}
              placeOfBirth={llmPlaceOfBirth}
              location={locationValue}
              imgInfinity={constants.images.imgInfinity}
              isLoading={isLoading}
            />
          </StyledContainer>
        </StyledCard>
        <FullObituary text={message} personName={name} />
        {isLoading && <ObituaryReportLoading />}
        <ObituarySmartInsight isLoading={isLoading} />
        <ObituaryRelatives relatives={llmRelatives} name={name} />
        <ObituaryLifeEvents
          llmAccomplishments={llmAccomplishments}
          llmEducation={llmEducation}
          llmCareer={llmCareer}
          name={name}
        />
        <ObituaryPersonalInterest
          activies={activities}
          hobbies={hobbies}
          miscellaneous={miscellaneous}
          personName={name}
        />
        <ObituaryFuneralDetails
          service={llmService}
          visitation={llmVisitation}
          donations={llmDonations}
          funeralHomeUrl={funeralHomeUrl}
        />
        <PersonSearchCta
          image={constants.images.imgPersonSearch}
          personName={name}
          location={locationValue}
        />
      </Grid>
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
    </>
  );
}
